import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../_hooks/use-auth'
import { MagicLinkCallbackState } from './types'
import { describeError, isMissingEmailError, looksLikeEmail } from './helpers'
import {
  PageContainer,
  ContentCard,
  Wordmark,
  Heading,
  Subheading,
  Form,
  Label,
  EmailInput,
  PrimaryButton,
  SecondaryButton,
  ErrorBanner,
  StatusIndicator,
  Spinner,
} from './styled'

/**
 * Lands here when a renter clicks the link in their magic-link email. On
 * mount, attempts to complete the Firebase sign-in using the pending email
 * stashed in localStorage. Three outcomes:
 *
 *   1. Success — Firebase auth state flips, redirect to `/` once
 *      `isAuthenticated` is true so AuthGate sees the signed-in user.
 *   2. Missing email (different device, cleared storage) — render a
 *      fallback form asking the renter to confirm the email they used.
 *   3. Other failure — show the error with a "back to sign-in" link.
 *
 * @returns {JSX.Element}
 */
const MagicLinkCallbackPage = (): JSX.Element => {
  const { completeMagicLinkSignIn, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const completionAttemptedReference = useRef<boolean>(false)
  const [state, setState] = useState<MagicLinkCallbackState>({
    status: 'completing',
    errorMessage: '',
  })
  const [fallbackEmail, setFallbackEmail] = useState<string>('')
  const [isSubmittingFallback, setIsSubmittingFallback] =
    useState<boolean>(false)

  const attemptCompletion = useCallback(
    async (suppliedEmail: string | null): Promise<void> => {
      try {
        await completeMagicLinkSignIn(window.location.href, suppliedEmail)
        setState({ status: 'success', errorMessage: '' })
      } catch (caughtError: unknown) {
        if (isMissingEmailError(caughtError)) {
          setState({ status: 'needs-email', errorMessage: '' })
          return
        }

        setState({
          status: 'error',
          errorMessage: describeError(
            caughtError,
            'We could not complete sign-in from this link.',
          ),
        })
      }
    },
    [completeMagicLinkSignIn],
  )

  useEffect(() => {
    if (completionAttemptedReference.current === true) {
      return
    }
    completionAttemptedReference.current = true

    void attemptCompletion(null)
  }, [attemptCompletion])

  useEffect(() => {
    if (state.status !== 'success' || isAuthenticated === false) {
      return
    }

    navigate('/', { replace: true })
  }, [state.status, isAuthenticated, navigate])

  const handleFallbackSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()

      if (looksLikeEmail(fallbackEmail) === false) {
        setState({
          status: 'needs-email',
          errorMessage: 'That does not look like a valid email address.',
        })
        return
      }

      setIsSubmittingFallback(true)
      setState({ status: 'completing', errorMessage: '' })

      try {
        await attemptCompletion(fallbackEmail.trim())
      } finally {
        setIsSubmittingFallback(false)
      }
    },
    [attemptCompletion, fallbackEmail],
  )

  const handleBackToSignIn = useCallback((): void => {
    navigate('/sign-in', { replace: true })
  }, [navigate])

  return (
    <PageContainer>
      <ContentCard>
        <Wordmark>QuickPass</Wordmark>

        {state.status === 'completing' ? (
          <>
            <Heading>Signing you in</Heading>
            <Subheading>
              Hold tight — finishing up your magic-link sign-in.
            </Subheading>
            <StatusIndicator>
              <Spinner
                animate={{
                  rotate: 360,
                  transition: {
                    repeat: Infinity,
                    duration: 0.7,
                    ease: 'linear',
                  },
                }}
              />
              <span>Completing sign-in&hellip;</span>
            </StatusIndicator>
          </>
        ) : null}

        {state.status === 'success' ? (
          <>
            <Heading>You&apos;re in</Heading>
            <Subheading>Taking you to your QuickPass&hellip;</Subheading>
            <StatusIndicator>
              <Spinner
                animate={{
                  rotate: 360,
                  transition: {
                    repeat: Infinity,
                    duration: 0.7,
                    ease: 'linear',
                  },
                }}
              />
            </StatusIndicator>
          </>
        ) : null}

        {state.status === 'needs-email' ? (
          <>
            <Heading>Confirm your email</Heading>
            <Subheading>
              Looks like you opened this link on a different device. Pop in the
              email you used to request it and we&apos;ll wrap up sign-in.
            </Subheading>

            <Form onSubmit={handleFallbackSubmit}>
              <Label htmlFor="magic-link-fallback-email">Your email</Label>
              <EmailInput
                id="magic-link-fallback-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={fallbackEmail}
                onChange={event => setFallbackEmail(event.target.value)}
                disabled={isSubmittingFallback}
                required
              />

              {state.errorMessage !== '' ? (
                <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
              ) : null}

              <PrimaryButton type="submit" disabled={isSubmittingFallback}>
                {isSubmittingFallback === true
                  ? 'Signing in…'
                  : 'Complete sign-in'}
              </PrimaryButton>
              <SecondaryButton type="button" onClick={handleBackToSignIn}>
                Back to sign-in
              </SecondaryButton>
            </Form>
          </>
        ) : null}

        {state.status === 'error' ? (
          <>
            <Heading>Sign-in failed</Heading>
            <Subheading>
              Something went sideways finishing your sign-in.
            </Subheading>
            <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
            <PrimaryButton type="button" onClick={handleBackToSignIn}>
              Back to sign-in
            </PrimaryButton>
          </>
        ) : null}
      </ContentCard>
    </PageContainer>
  )
}

export default MagicLinkCallbackPage
